import { Component, DestroyRef, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
} from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzUploadComponent, NzUploadFile } from 'ng-zorro-antd/upload';
import { LoaderService } from '../../../../../core/services/loader.service';
import {
  BehaviorSubject,
  debounceTime,
  finalize,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LogoState, OrganizationLogo } from '../../settings.interface';
import { SettingsService } from '../../settings.service';
import { CommonModule } from '@angular/common';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzInputModule } from 'ng-zorro-antd/input';
import { ActivatedRoute } from '@angular/router';
import { ConfirmDialogComponent } from '../../../../../components/confirm-dialog/confirm-dialog.component';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { ErrorResponse } from '../../../../../core/models/error.models';
import { CommonService } from '../../../../../shared/services/common.service';
import { ManageOrganizationDrawerParams } from '../../../manage-organization.interface';
import { OrgManagementMode } from '../../../manage-organization.enums';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { AuthService } from '../../../../../core/services/auth.service';
import { OrganizationDetailModel } from '../../../../organizations/organization.interface';
import { OrganizationSelectionService } from '../../../../../core/services/organization-selection.service';

@Component({
  selector: 'app-branding-logo',
  standalone: true,
  imports: [
    FormsModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzSelectModule,
    NzUploadComponent,
    SvgIconComponent,
    NzDropDownModule,
    NzButtonModule,
    CommonModule,
    ReactiveFormsModule,
    NzInputModule,
    ConfirmDialogComponent,
    NzSpinModule,
  ],
  templateUrl: './branding-logo.component.html',
  styleUrl: './branding-logo.component.scss',
})
export class BrandingLogoComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly settingsService = inject(SettingsService);
  private readonly fb = inject(FormBuilder);
  private readonly loaderService = inject(LoaderService);
  private readonly message = inject(NzMessageService);
  private readonly commonService = inject(CommonService);
  private readonly SELECTED_ORG = 'Selected_Org_Data';

  readonly logos$ = new BehaviorSubject<OrganizationLogo[]>([]);
  private previousState = new Map<string, LogoState>();

  form: FormGroup;
  fileList: NzUploadFile[] = [];
  selectedLogo: string | null = null;
  orgId = 0;
  orgParams: ManageOrganizationDrawerParams = {
    organizationId: 0,
    mode: OrgManagementMode.VIEW,
  };
  isEditing = false;
  isCreateMode = false;
  showConfirmDialog = false;
  private readonly destroy$ = new Subject<void>();
  loading$: Observable<boolean> = of(false);
  orgData: OrganizationDetailModel | null = null;
  constructor(
    private route: ActivatedRoute,
    private drawerService: DrawerService,
    private authService: AuthService,
    private orgSelectionService: OrganizationSelectionService
  ) {
    this.setupDrawerParams();
    this.form = this.createForm();
    this.loadLogos();
    this.loading$ = this.loaderService.loading$;
  }

  private setupDrawerParams(): void {
    this.drawerService
      .getParams<ManageOrganizationDrawerParams>(DRAWER_IDS.MANAGE_ORG)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.orgParams = params;
        } else {
          this.orgSelectionService.orgId$
            .pipe(debounceTime(1000), takeUntil(this.destroy$))
            .subscribe(res => {
              this.orgParams.organizationId = res || 0;

              this.loadLogos();
            });
          // this.authService.orgData$.subscribe(res => {
          //   this.orgData = res;
          //   this.orgParams.organizationId = this.orgData?.organizationId || 0;
          // });
        }
      });
  }

  private createForm(): FormGroup {
    const namePattern = new RegExp(
      '^[a-zA-Z0-9][a-zA-Z0-9\\s_\\-.,]*[a-zA-Z0-9]$'
    );
    return this.fb.group({
      brandingName: [null],
      productName: [
        { value: '', disabled: true },
        [Validators.required, Validators.pattern(namePattern)],
      ],
      file: [null, [Validators.required]],
    });
  }

  beforeUpload = (file: NzUploadFile): boolean => {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
    const maxSize = 2 * 1024 * 1024;

    if (
      !this.isValidFileType(file, validTypes) ||
      !this.isValidFileSize(file, maxSize)
    ) {
      return false;
    }

    this.fileList = [file];
    return false;
  };

  private isValidFileType(file: NzUploadFile, validTypes: string[]): boolean {
    if (!validTypes.includes(file.type!)) {
      this.message.error('You can only upload JPG, PNG, or GIF files.');
      return false;
    }
    return true;
  }

  private isValidFileSize(file: NzUploadFile, maxSize: number): boolean {
    if (file.size! >= maxSize) {
      this.message.error('Image must be smaller than 2MB.');
      return false;
    }
    return true;
  }

  private loadLogos(): void {
    this.loaderService.show();
    this.settingsService
      .getOrganizationLogos(this.orgParams.organizationId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: response => {
          this.logos$.next(response);
          this.setActive(response);
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify(
            'error',
            !errorMessages.length
              ? 'No Organization Found with Id 0'
              : errorMessages,
            message
          );
        },
      });
  }

  private setActive(logos: OrganizationLogo[]): void {
    const active = logos.find(logo => logo.isActive) || logos[0];

    if (!active) {
      this.selectedLogo = 'assets/images/logo-lg.png';
      return;
    }

    if (logos.length > 0 && !logos.some(logo => logo.isActive)) {
      active.isActive = true;
    }

    this.selectedLogo = active.logoUrl;
    if (this.orgData) {
      this.orgData.logoUrl = active.logoUrl;
      this.authService.orgData.next(this.orgData);
      localStorage.setItem(
        this.SELECTED_ORG,
        this.authService.encryptData(this.orgData)
      );
    }

    this.form.patchValue({
      brandingName: active.id,
      productName: active.productName,
    });
  }

  onBrandingChange(selectedId: number): void {
    if (!selectedId) return;

    this.logos$
      .pipe(
        take(1),
        map(logos => logos.find(logo => logo.isActive)?.id)
      )
      .subscribe(currentActiveId => {
        if (currentActiveId !== selectedId) {
          this.loaderService.show();
          this.settingsService
            .updateOrganizationActiveLogo(
              this.orgParams.organizationId,
              selectedId
            )
            .pipe(
              takeUntilDestroyed(this.destroyRef),
              finalize(() => this.loaderService.hide())
            )
            .subscribe({
              next: () => {
                this.logos$.pipe(take(1)).subscribe(currentLogos => {
                  const updatedLogos = currentLogos.map(logo => ({
                    ...logo,
                    isActive: logo.id === selectedId,
                  }));
                  this.logos$.next(updatedLogos);
                  this.setActive(updatedLogos);
                });
                this.commonService.notify(
                  'success',
                  'Logo activated successfully',
                  '',
                  '450px'
                );
              },
              error: ({ errors, message }: ErrorResponse) => {
                const errorMessages = errors.map(
                  err => err.description || err.detailedMessage
                );
                this.commonService.notify('error', errorMessages, message);
              },
            });
        }
      });
  }

  handleUpload(): void {
    const file = this.fileList[0] as unknown as File;

    if (!file && !this.isEditing) {
      this.commonService.notify('error', ['Please select a file.']);
      return;
    }

    this.loaderService.show();
    const { productName, brandingName } = this.form.value;

    const action$ = this.isEditing
      ? this.settingsService.updateLogo(
          brandingName,
          productName,
          this.orgParams.organizationId,
          file
        )
      : this.settingsService.createOrganizationLogo(
          this.orgParams.organizationId,
          productName,
          file
        );

    action$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(result =>
          this.settingsService.updateOrganizationActiveLogo(
            this.orgParams.organizationId,
            result.id
          )
        ),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: () => {
          this.loadLogos();
          this.commonService.notify(
            'success',
            `Logo ${this.isEditing ? 'updated' : 'created'} and activated successfully`,
            '',
            '450px'
          );
          this.resetUploadState();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  private resetUploadState(): void {
    this.fileList = [];
    this.form.get('brandingName')?.enable();
    this.form.get('productName')?.disable();
    this.isCreateMode = false;
    this.isEditing = false;
  }

  handleFileRemove = (file: NzUploadFile): boolean => {
    this.fileList = this.fileList.filter(f => f !== file);
    return false;
  };

  get showCreateButton(): boolean {
    return (
      (this.logos$.value?.length < 2 && !this.isEditing) || this.isCreateMode
    );
  }

  get showEditButton(): boolean {
    return !this.isCreateMode || this.isEditing;
  }

  get showCancelLogoBtn(): boolean {
    return this.isCreateMode || this.isEditing;
  }

  handleCreate(): void {
    if (this.isCreateMode) {
      this.handleUpload();
      return;
    }
    this.initCreateMode();
  }

  handleEdit(): void {
    if (this.isEditing) {
      this.handleUpload();
      return;
    }
    this.form.get('productName')?.enable();
    this.isEditing = true;
  }

  private saveState(key: 'edit' | 'create'): void {
    this.previousState.set(key, {
      productName: this.form.get('productName')?.value,
      brandingName: this.form.get('brandingName')?.value,
      selectedLogo: this.selectedLogo,
    });
  }

  private restoreState(key: 'edit' | 'create'): void {
    const state = this.previousState.get(key);
    if (state) {
      this.form.patchValue({
        productName: state.productName,
        brandingName: state.brandingName,
      });
      this.selectedLogo = state.selectedLogo;
      this.previousState.delete(key);
    }
  }

  private initCreateMode(): void {
    this.saveState('create');
    this.resetFormState();
    this.form.get('productName')?.enable();
    this.form.get('brandingName')?.disable();
    this.isCreateMode = true;
  }

  handleCancel(): void {
    if (!this.isCreateMode && !this.isEditing) return;

    const stateKey = this.isCreateMode ? 'create' : 'edit';
    this.restoreState(stateKey);
    this.resetFormControls();
  }

  private resetFormState(): void {
    this.form.reset();
    this.fileList = [];
    this.selectedLogo = null;
    this.isEditing = false;
  }

  private resetFormControls(): void {
    this.isCreateMode = false;
    this.isEditing = false;
    this.fileList = [];
    this.form.get('brandingName')?.enable();
    this.form.get('productName')?.disable();
  }

  handleDelete(): void {
    const logoId = this.form.get('brandingName')?.value;
    if (!logoId) {
      this.message.error('No logo selected to delete');
      return;
    }

    this.loaderService.show();
    this.settingsService
      .deleteOrganizationLogo(logoId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loaderService.hide();
          this.handleClose();
        }),
        tap(() => {
          this.selectedLogo = null;
        }),
        switchMap(() =>
          this.settingsService.getOrganizationLogos(
            this.orgParams.organizationId
          )
        )
      )
      .subscribe({
        next: response => {
          this.logos$.next(response);
          this.setActive(response);
          this.resetUploadState();
          this.commonService.notify(
            'success',
            'Logo deleted successfully',
            '',
            '450px'
          );
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  handleClose(): void {
    this.showConfirmDialog = false;
  }
}
